import {ClickEvent, Tracker, TrackImpression} from '@udemy/event-tracking';
import {
    DateIntervalType,
    SubscriptionPlanDiscountType,
    SubscriptionPlanPriceDiscountData,
    SubscriptionPlanProductType,
    SubscriptionPlanTrialDiscountData,
} from '@udemy/graphql';
import {useI18n} from '@udemy/i18n';
import {SpotIllustration} from '@udemy/illustrations';
import {patternBackground} from '@udemy/react-brand-components';
import {Banner, BannerButtonStack, BannerTitle} from '@udemy/react-messaging-components';
import {SubscriptionOfferPresented} from '@udemy/subscription-browse';
import {
    ConsumerSubscriptionPlan,
    SubscriptionPlansProvider,
    useConsumerSubscriptionPlan,
} from '@udemy/subscription-graphql';
import {useUDData} from '@udemy/ud-data';

import {useLocalStorageDismiss} from 'src/hooks/use-local-storage-dismiss';

const UI_REGION = 'lihp_mx_upsell_modal';
const PERSONAL_PLAN_UPSELL_DISMISSED_KEY = 'personal_plan_upsell_banner_dismissed';
const PERSONAL_PLAN_UPSELL_DISMISS_TTL_DAYS = 1;

const publishSubscriptionOfferPresentedEvent = (
    subscriptionPlan: ConsumerSubscriptionPlan,
    countryCode: string,
) => {
    Tracker.publishEvent(
        new SubscriptionOfferPresented({
            subscriptionPlan,
            productType: SubscriptionPlanProductType.Consumersubscription,
            countryCode,
            uiRegion: UI_REGION,
        }),
    );
};

const trackClickEvent = (event: string, trackingId: string) => {
    Tracker.publishEvent(new ClickEvent({componentName: event, trackingId}));
};

const PersonalPlanUpsellBannerInner = () => {
    const {gettext, interpolate} = useI18n();
    const [isDismissed, dismissBanner] = useLocalStorageDismiss(
        PERSONAL_PLAN_UPSELL_DISMISSED_KEY,
        {ttlDays: PERSONAL_PLAN_UPSELL_DISMISS_TTL_DAYS},
    );
    const personalPlan = useConsumerSubscriptionPlan();
    const udData = useUDData();

    if (personalPlan?.isLoading || isDismissed || personalPlan === undefined) {
        return null;
    }

    const getTrialDiscountTexts = (data: SubscriptionPlanTrialDiscountData) => {
        return {
            titleSuffix: interpolate(
                gettext('Free for %(duration)s days'),
                {duration: data.days},
                true,
            ),
            ctaText: gettext('Try for free'),
            ctaLink: `/subscription-checkout/express/?product_type=CONSUMERSUBSCRIPTION&plan_preference=YEAR`,
        };
    };

    const getAnnualIntervalText = (duration: number) => {
        if (duration === 1) {
            return gettext('first year');
        }
        return interpolate(gettext('first %(duration)s years'), {duration}, true);
    };

    const getPriceDiscountTexts = (data: SubscriptionPlanPriceDiscountData) => {
        const intervalText =
            data.interval.type === DateIntervalType.Month
                ? data.interval.duration === 1
                    ? gettext('1 month')
                    : interpolate(
                          gettext('%(interval)s months'),
                          {interval: data.interval.duration},
                          true,
                      )
                : getAnnualIntervalText(data.interval.duration);
        return {
            titleSuffix:
                interpolate(
                    gettext('%(percentage)s% off for '),
                    {
                        percentage: data.value,
                    },
                    true,
                ) + intervalText,
            ctaText: gettext('Get Personal Plan'),
            ctaLink: `/subscription-checkout/express/?product_type=CONSUMERSUBSCRIPTION&plan_preference=${data.interval.type === DateIntervalType.Month ? 'MONTH' : 'YEAR'}`,
        };
    };

    if (personalPlan.isSubscriber || !personalPlan.priceOptions?.length) {
        return null;
    }

    const plan = personalPlan as ConsumerSubscriptionPlan;
    const appliedDiscount = plan.priceOptions
        .flatMap((option) => option.discount?.appliedDiscounts ?? [])
        .find((discount) => discount.code.startsWith('MXUPSELL'));

    if (!appliedDiscount) {
        return null;
    }
    const isTrialDiscount =
        appliedDiscount?.discountType === SubscriptionPlanDiscountType.TrialDiscount;
    const {titleSuffix, ctaText, ctaLink} = isTrialDiscount
        ? getTrialDiscountTexts(appliedDiscount?.data as SubscriptionPlanTrialDiscountData)
        : getPriceDiscountTexts(appliedDiscount?.data as SubscriptionPlanPriceDiscountData);

    const handleImpression = () => {
        publishSubscriptionOfferPresentedEvent(plan, udData.Config.price_country?.id ?? '');
    };

    return (
        <TrackImpression trackFunc={handleImpression}>
            <div className={'ud-container'}>
                <Banner
                    backgroundStyle={{
                        ...patternBackground({
                            color: 'color-purple-150',
                            pattern: 'wavy-lines',
                            colorScheme: 'light',
                            inverted: false,
                        }),
                        backgroundColor: '#F2EFFF',
                    }}
                    startImageArea={
                        <SpotIllustration alt="" height={120} name="dynamic-team" width={120} />
                    }
                    onDismissClick={() => {
                        trackClickEvent(
                            'PersonalPlanUpsellBannerDismiss',
                            appliedDiscount?.code ?? '',
                        );
                        dismissBanner();
                    }}
                    title={
                        <BannerTitle titleStyle="ud-heading-lg">
                            {gettext('Build the skills that move you forwards. ')}
                            {titleSuffix}
                        </BannerTitle>
                    }
                    ctaButtons={
                        <BannerButtonStack
                            primaryButtonText={ctaText}
                            primaryOnClick={() => {
                                trackClickEvent(
                                    'PersonalPlanUpsellBannerCTA',
                                    appliedDiscount?.code ?? '',
                                );
                                window.location.href = ctaLink;
                            }}
                            primaryButtonProps={{
                                componentClass: 'a',
                                href: ctaLink,
                            }}
                        />
                    }
                >
                    {gettext(
                        'Join 550K+ subscribers building, practicing, and validating the skills that move their careers forward with Personal Plan.',
                    )}
                </Banner>
            </div>
        </TrackImpression>
    );
};

export const PersonalPlanUpsellBanner = () => {
    return (
        <SubscriptionPlansProvider
            subscriptionPlanQueryVariables={[
                {filters: {productType: SubscriptionPlanProductType.Consumersubscription}},
            ]}
        >
            <PersonalPlanUpsellBannerInner />
        </SubscriptionPlansProvider>
    );
};
